import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, Network, Users, Building2, Briefcase, 
  AlertTriangle, Shield, GraduationCap, BarChart3, Settings,
  Menu, X, LogOut, ChevronDown, ChevronRight, HardHat, Upload, FolderOpen, MapPin, Layers, Wrench, Building, Activity, FileText, CalendarDays, Repeat
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    name: 'Organizacion', icon: Network, isGroup: true,
    children: [
      { name: 'Usuarios', href: '/usuarios', icon: Users, adminOnly: true },
      { name: 'Organigrama', href: '/organigrama', icon: Network },
      { name: 'Mapa de Areas', href: '/mapa', icon: MapPin },
      { name: 'Personas', href: '/personas', icon: Users },
    ]
  },
  {
    name: 'Empresas', icon: Building, isGroup: true,
    children: [
      { name: 'Empresas Colaboradoras', href: '/empresas-colaboradoras', icon: Building },
    ]
  },
  {
    name: 'Seguridad y Riesgos', icon: Shield, isGroup: true,
    children: [
      { name: 'QRA', href: '/qra', icon: Shield },
      { name: 'Riesgos y Controles', href: '/controles', icon: AlertTriangle },
    ]
  },
  {
    name: 'Capacitacion', icon: GraduationCap, isGroup: true,
    children: [
      { name: 'Capacitaciones', href: '/capacitaciones', icon: GraduationCap },
      { name: 'Calendario Cap.', href: '/calendario-capacitaciones', icon: CalendarDays },
    ]
  },
  {
    name: 'Info GCOM', icon: Activity, isGroup: true,
    children: [
      { name: 'Informe GCOM', href: '/informe-gcom', icon: Activity },
      { name: 'GCOM', href: '/reportes-gcom', icon: FileText },
    ]
  },
  {
    name: 'Mantenedores', icon: Wrench, isGroup: true,
    children: [
      { name: 'Areas', href: '/areas', icon: Building2 },
      { name: 'Sectores', href: '/sectores', icon: MapPin },
      { name: 'Cargos', href: '/cargos', icon: Briefcase },
      { name: 'Organizacion', href: '/organizacion', icon: Layers },
      { name: 'Actividades', href: '/actividades', icon: HardHat },
    ]
  },
  { name: 'Repositorio', href: '/repositorio', icon: FolderOpen },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Carga Masiva', href: '/carga-masiva', icon: Upload, adminOnly: true },
  { name: 'Configuracion', href: '/configuracion', icon: Settings, adminOnly: true },
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));
  const [openGroups, setOpenGroups] = useState({});
  const { user, logout, isAdmin, hasMultiplePerfiles, switchPerfil } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleGroup = (name) => setOpenGroups(p => ({ ...p, [name]: !p[name] }));

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/configuracion`);
      if (res.data.logo_image) {
        let imgUrl = res.data.logo_image;
        if (imgUrl.startsWith('/api/uploads/')) {
          imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
        }
        setLogoImage(imgUrl);
        localStorage.setItem('cached_logo_url', imgUrl);
      }
    } catch (e) {
      console.log('Config not available');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNav = navigation.filter(item => item.isGroup || !item.adminOnly || isAdmin());

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo Header - Aligned with main header (h-16 = 64px) */}
        <div className="h-16 border-b border-slate-100 flex items-center px-2">
          <Link to="/dashboard" className="flex-1 flex items-center justify-center">
            {logoImage ? (
              <div className="w-[232px] h-[56px] overflow-hidden flex items-center justify-center">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain"
                  onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }}
                  data-testid="sidebar-logo"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                  <Network className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900 tracking-tight">Organigrama</span>
              </div>
            )}
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          {filteredNav.map((item) => {
            if (item.isGroup) {
              const childActive = item.children.some(c => location.pathname === c.href);
              const isOpen = openGroups[item.name] || childActive;
              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleGroup(item.name)}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      childActive
                        ? 'bg-teal-50 text-teal-600'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 ${childActive ? 'text-teal-500' : 'text-slate-400'}`} />
                    <span className="flex-1 text-left text-[13px]">{item.name}</span>
                    <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-slate-100 pl-2">
                      {item.children.filter(c => !c.adminOnly || isAdmin()).map((child) => {
                        const isChildActive = location.pathname === child.href;
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            data-testid={`nav-${child.name.toLowerCase().replace(/\s/g, '-')}`}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-all ${
                              isChildActive
                                ? 'bg-teal-50 text-teal-600 font-medium'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <child.icon className={`w-3.5 h-3.5 ${isChildActive ? 'text-teal-500' : 'text-slate-400'}`} />
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                  isActive 
                    ? 'bg-teal-50 text-teal-600 border-l-4 border-teal-500' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`w-4 h-4 ${isActive ? 'text-teal-500' : 'text-slate-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            data-testid="menu-toggle"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-teal-600">
                    {user?.nombre?.[0]}{user?.apellido?.[0]}
                  </span>
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700">
                  {user?.nombre} {user?.apellido}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-slate-600">
                <span className="text-xs uppercase tracking-wider text-slate-400">Rol:</span>
                <span className="ml-2 capitalize">{user?.tipo === 'key_user' ? 'Key User' : user?.rol}</span>
              </DropdownMenuItem>
              {hasMultiplePerfiles && hasMultiplePerfiles() && (
                <DropdownMenuItem
                  onClick={() => { if (switchPerfil()) navigate('/seleccionar-perfil'); }}
                  className="text-amber-700"
                  data-testid="cambiar-perfil-btn">
                  <Repeat className="w-4 h-4 mr-2" />
                  Cambiar de perfil
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 text-center text-sm text-slate-400 border-t border-slate-200">
          © TystaCore {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
};
